import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import UserContext from "../auth/UserContext";
import ValetApi from "../api/Api";

const APP_SETTINGS_STORAGE_KEY = "parkpilot.appSettings.v1";
const TENANT_THEME_STORAGE_KEY = "parkpilot.tenantThemes.v1";

const defaultSettings = {
    themeMode: "auto", // auto | light | dark
    branding: {
        brandText: "ParkPilot",
        logoType: "text", // text | image
        logoUrl: "",
    },
    garageDetail: {
        maskPhoneFirstSeven: false,
        showFieldLabels: true,
        visibleFields: {
            podium: true,
            ticket: true,
            mobile: true,
            make: true,
            color: true,
            parkingSpot: true,
            note: true,
            status: true,
            payment: true,
        },
    },
};

const AppSettingsContext = createContext({
    settings: defaultSettings,
    setThemeMode: () => {},
    setBranding: () => {},
    setGarageDetailSetting: () => {},
    setGarageFieldVisibility: () => {},
    resetAppSettings: () => {},
});

function readStoredSettings() {
    try {
        const raw = window.localStorage.getItem(APP_SETTINGS_STORAGE_KEY);
        if (!raw) return defaultSettings;

        const parsed = JSON.parse(raw);
        return {
            ...defaultSettings,
            ...parsed,
            branding: {
                ...defaultSettings.branding,
                ...(parsed?.branding || {}),
            },
            garageDetail: {
                ...defaultSettings.garageDetail,
                ...(parsed?.garageDetail || {}),
                visibleFields: {
                    ...defaultSettings.garageDetail.visibleFields,
                    ...(parsed?.garageDetail?.visibleFields || {}),
                },
            },
        };
    } catch {
        return defaultSettings;
    }
}

function resolveThemeMode(themeMode) {
    if (themeMode === "light" || themeMode === "dark") return themeMode;

    const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    return prefersDark ? "dark" : "light";
}

export function AppSettingsProvider({ children }) {
    const { token, userDetail } = useContext(UserContext);
    const location = useLocation();
    const [settings, setSettings] = useState(readStoredSettings);
    const [tenantThemes, setTenantThemes] = useState(() => {
        try {
            const stored = JSON.parse(window.localStorage.getItem(TENANT_THEME_STORAGE_KEY) || "{}");
            return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
        } catch {
            return {};
        }
    });
    const [hasHydratedFromServer, setHasHydratedFromServer] = useState(false);
    const skipNextServerSaveRef = useRef(false);

    const username = String(userDetail?.username || "")
        .trim()
        .toLowerCase();
    const isPublicCustomerRoute = String(location?.pathname || "").startsWith("/customers") || String(location?.pathname || "").startsWith("/survey/customer");
    const isAuthenticatedUser = Boolean(token && username);
    const isTenantPortal = Boolean(userDetail?.isTenantPortal);
    const tenantThemeKey = String(userDetail?.tenantId || username);
    const storedTenantTheme = tenantThemes[tenantThemeKey];
    const tenantThemeMode = storedTenantTheme === "light" || storedTenantTheme === "dark" ? storedTenantTheme : "auto";
    const themeMode = isTenantPortal ? tenantThemeMode : settings.themeMode;
    const canSyncSettingsWithServer = isAuthenticatedUser && !isTenantPortal && !isPublicCustomerRoute;

    useEffect(() => {
        let cancelled = false;

        async function hydrateSettingsFromServer() {
            if (!canSyncSettingsWithServer) {
                setHasHydratedFromServer(true);
                return;
            }

            try {
                const res = await ValetApi.getAppSettingsByUsername(username);
                if (cancelled) return;

                const serverSettings = res?.data?.settings || {};
                const merged = {
                    ...defaultSettings,
                    ...(serverSettings || {}),
                    branding: {
                        ...defaultSettings.branding,
                        ...(serverSettings?.branding || {}),
                    },
                    garageDetail: {
                        ...defaultSettings.garageDetail,
                        ...(serverSettings?.garageDetail || {}),
                        visibleFields: {
                            ...defaultSettings.garageDetail.visibleFields,
                            ...(serverSettings?.garageDetail?.visibleFields || {}),
                        },
                    },
                };

                skipNextServerSaveRef.current = true;
                setSettings(merged);
                setHasHydratedFromServer(true);
            } catch {
                if (cancelled) return;
                setHasHydratedFromServer(true);
            }
        }

        setHasHydratedFromServer(false);
        hydrateSettingsFromServer();

        return () => {
            cancelled = true;
        };
    }, [canSyncSettingsWithServer, username]);

    useEffect(() => {
        window.localStorage.setItem(APP_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    }, [settings]);

    useEffect(() => {
        window.localStorage.setItem(TENANT_THEME_STORAGE_KEY, JSON.stringify(tenantThemes));
    }, [tenantThemes]);

    useEffect(() => {
        let cancelled = false;

        async function saveSettingsToServer() {
            if (!canSyncSettingsWithServer || !hasHydratedFromServer) return;

            if (skipNextServerSaveRef.current) {
                skipNextServerSaveRef.current = false;
                return;
            }

            try {
                await ValetApi.saveAppSettingsByUsername(username, settings);
            } catch {
                if (cancelled) return;
            }
        }

        saveSettingsToServer();

        return () => {
            cancelled = true;
        };
    }, [settings, canSyncSettingsWithServer, hasHydratedFromServer, username]);

    useEffect(() => {
        const applyTheme = () => {
            const resolved = resolveThemeMode(themeMode);
            document.documentElement.setAttribute("data-theme", resolved);
        };

        applyTheme();

        const mediaQuery = window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;
        const onChange = () => {
            if (themeMode === "auto") {
                applyTheme();
            }
        };

        if (mediaQuery && mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", onChange);
            return () => mediaQuery.removeEventListener("change", onChange);
        }

        return undefined;
    }, [themeMode]);

    const value = useMemo(() => {
        return {
            settings: isTenantPortal ? { ...settings, themeMode } : settings,
            setThemeMode: (themeMode) => {
                if (!["auto", "light", "dark"].includes(themeMode)) return;
                if (isTenantPortal) {
                    setTenantThemes((prev) => ({ ...prev, [tenantThemeKey]: themeMode }));
                    return;
                }
                setSettings((prev) => ({ ...prev, themeMode }));
            },
            setBranding: (nextBranding) => {
                setSettings((prev) => ({
                    ...prev,
                    branding: {
                        ...prev.branding,
                        ...nextBranding,
                    },
                }));
            },
            setGarageDetailSetting: (patch) => {
                setSettings((prev) => ({
                    ...prev,
                    garageDetail: {
                        ...prev.garageDetail,
                        ...patch,
                    },
                }));
            },
            setGarageFieldVisibility: (fieldKey, isVisible) => {
                setSettings((prev) => ({
                    ...prev,
                    garageDetail: {
                        ...prev.garageDetail,
                        visibleFields: {
                            ...prev.garageDetail.visibleFields,
                            [fieldKey]: Boolean(isVisible),
                        },
                    },
                }));
            },
            resetAppSettings: () => setSettings(defaultSettings),
        };
    }, [settings, isTenantPortal, themeMode, tenantThemeKey]);

    return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
}

export function useAppSettings() {
    return useContext(AppSettingsContext);
}
